import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import {
  calendarizacaoToSalvarCommand,
  configuracaoToSalvarCommand,
  extratoDtoToConfiguracao,
  extratoDtoToExtratoDesligamento,
  extratoDtoToUltimaAlteracao,
  notificacoesDtoToCalendarizacao,
  notificacoesDtoToUltimaAlteracao,
} from '../../mappers/parametros/extrato-desligamento-api.mapper';
import {
  AlteracaoExtrato,
  CalendarizacaoNotificacoes,
  ExtratoDesligamento,
  ExtratoDesligamentoConfiguracao,
  OpcaoSituacao,
} from '../../models/parametros/extrato-desligamento.model';
import { ExtratoDesligamentoApiService } from './extrato-desligamento-api.service';

/** Só os registros ativos dos catálogos viram opção na tela. */
const CATALOGO_ATIVO = { ativo: 'S' };

/**
 * Parametrização do extrato de desligamento de cada plano: elegibilidade e calendarização das
 * notificações.
 * A elegibilidade vem de `/v1/planos-param/{id}/extrato-desligamento`, com as situações cadastrais
 * e as condições de elegibilidade dos catálogos da API. A calendarização vem de
 * `/v1/planos-param/{id}/extrato-desligamento/notificacoes`. As situações cadastrais são de cada
 * instituto.
 */
@Injectable({
  providedIn: 'root',
})
export class ExtratoDesligamentoService {
  private readonly apiService = inject(ExtratoDesligamentoApiService);

  obter(planoId: number): Observable<ExtratoDesligamento> {
    return forkJoin({
      extrato: this.apiService.obterExtrato(planoId),
      situacoes: this.apiService.listarSituacoesCadastrais(CATALOGO_ATIVO),
      condicoes: this.apiService.listarCondicoesElegibilidade(CATALOGO_ATIVO),
    }).pipe(map(({ extrato, situacoes, condicoes }) => extratoDtoToExtratoDesligamento(extrato, situacoes, condicoes)));
  }

  /**
   * Grava a parametrização inteira e devolve o que a API gravou, com as situações de cada instituto
   * na ordem das opções da tela.
   */
  salvar(
    planoId: number,
    configuracao: ExtratoDesligamentoConfiguracao,
    situacoes: readonly OpcaoSituacao[],
  ): Observable<Pick<ExtratoDesligamento, 'configuracao' | 'ultimaAlteracao'>> {
    return this.apiService.salvarExtrato(planoId, configuracaoToSalvarCommand(configuracao)).pipe(
      map((dto) => ({
        configuracao: extratoDtoToConfiguracao(dto, situacoes),
        ultimaAlteracao: extratoDtoToUltimaAlteracao(dto),
      })),
    );
  }

  /**
   * Calendarização das notificações de prazo de adesão (aba "Calendarização de Notificações").
   * Plano ainda sem extrato vem com tudo desligado.
   */
  obterNotificacoes(planoId: number): Observable<CalendarizacaoNotificacoes> {
    return this.apiService.obterNotificacoes(planoId).pipe(map(notificacoesDtoToCalendarizacao));
  }

  /** Grava a calendarização e devolve o que a API gravou, com a última alteração. */
  salvarNotificacoes(
    planoId: number,
    notificacoes: CalendarizacaoNotificacoes,
  ): Observable<{ notificacoes: CalendarizacaoNotificacoes; ultimaAlteracao: AlteracaoExtrato | null }> {
    return this.apiService.salvarNotificacoes(planoId, calendarizacaoToSalvarCommand(notificacoes)).pipe(
      map((dto) => ({
        notificacoes: notificacoesDtoToCalendarizacao(dto),
        ultimaAlteracao: notificacoesDtoToUltimaAlteracao(dto),
      })),
    );
  }
}
